import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Grid,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  PauseCircle as PauseCircleIcon,
  PlayCircle as PlayCircleIcon,
  VisibilityOff as VisibilityOffIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useNotifications } from '../context/NotificationContext';
import ListingProductSidebar from '../components/common/ListingProductSidebar';
import activitySuspensionService from '../services/activitySuspensionService';

const statCardSx = {
  borderRadius: 3,
  border: '1px solid rgba(15, 23, 42, 0.08)',
  boxShadow: '0 18px 40px rgba(15, 23, 42, 0.08)'
};

const ActivitySuspensionPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation(['common', 'products']);
  const { user } = useAuth();
  const { showNotification } = useNotifications();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState(null);
  const [reason, setReason] = useState('');

  const loadStatus = async () => {
    try {
      setLoading(true);
      const data = await activitySuspensionService.getStatus();
      setStatus(data);
      if (data?.isSuspended && data?.reason) {
        setReason(data.reason);
      }
    } catch (error) {
      console.error('[ActivitySuspension] loadStatus error:', error?.response?.data || error);
      showNotification(
        error?.response?.data?.message || t('activitySuspension.errors.load', 'Impossible de charger le statut de suspension.'),
        'error'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStatus();
  }, []);

  const handleSuspend = async () => {
    console.log('[ActivitySuspension] handleSuspend called, reason:', reason);
    try {
      setSubmitting(true);
      const data = await activitySuspensionService.suspend(reason);
      console.log('[ActivitySuspension] suspend response:', data);
      setStatus(data);
      await loadStatus();
      showNotification(
        t('activitySuspension.success.suspended', 'Votre activité a été suspendue. Vos annonces et produits ne sont plus visibles publiquement.'),
        'success'
      );
    } catch (error) {
      console.error('[ActivitySuspension] suspend error:', error?.response?.data || error);
      showNotification(
        error?.response?.data?.message || t('activitySuspension.errors.suspend', 'La suspension de votre activité a échoué.'),
        'error'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleReactivate = async () => {
    console.log('[ActivitySuspension] handleReactivate called, reason:', reason);
    try {
      setSubmitting(true);
      const data = await activitySuspensionService.reactivate(reason);
      console.log('[ActivitySuspension] reactivate response:', data);
      setStatus(data);
      await loadStatus();
      showNotification(
        t('activitySuspension.success.reactivated', 'Votre activité a été réactivée. Vos annonces et produits suspendus volontairement sont à nouveau visibles.'),
        'success'
      );
    } catch (error) {
      console.error('[ActivitySuspension] reactivate error:', error?.response?.data || error);
      showNotification(
        error?.response?.data?.message || t('activitySuspension.errors.reactivate', 'La réactivation de votre activité a échoué.'),
        'error'
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/dashboard')} sx={{ mb: 3 }}>
        {t('common.back', 'Retour')}
      </Button>

      <Card sx={{ ...statCardSx, mb: 3, overflow: 'hidden' }}>
        <Box
          sx={{
            p: 3,
            color: 'white',
            background: status?.isSuspended
              ? 'linear-gradient(135deg, #b45309 0%, #ea580c 100%)'
              : 'linear-gradient(135deg, #0f766e 0%, #14b8a6 100%)'
          }}
        >
          <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2}>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
                {t('activitySuspension.title', 'Suspension de mon activité')}
              </Typography>
              <Typography sx={{ opacity: 0.92, maxWidth: 780 }}>
                {t(
                  'activitySuspension.subtitle',
                  "Cette action n'est pas un bannissement. Elle masque simplement vos annonces et produits au public le temps de votre indisponibilité, puis vous permet de les réactiver quand vous le souhaitez."
                )}
              </Typography>
            </Box>
            <Chip
              icon={status?.isSuspended ? <PauseCircleIcon /> : <PlayCircleIcon />}
              label={status?.isSuspended
                ? t('activitySuspension.status.suspended', 'Activité suspendue')
                : t('activitySuspension.status.active', 'Activité active')}
              sx={{
                alignSelf: 'flex-start',
                bgcolor: 'rgba(255,255,255,0.18)',
                color: 'white',
                fontWeight: 700,
                '& .MuiChip-icon': { color: 'white' }
              }}
            />
          </Stack>
        </Box>
        <CardContent sx={{ p: 3 }}>
          <Alert severity="info" sx={{ mb: 3 }}>
            {t(
              'activitySuspension.info',
              "Seule la visibilité publique est impactée. Le mécanisme est proche du ban sur ce point précis, mais il s'agit ici d'une action volontaire initiée par vous."
            )}
          </Alert>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <Card sx={statCardSx}>
                <CardContent>
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
                    <VisibilityOffIcon color="warning" />
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {t('activitySuspension.stats.listings', 'Annonces suspendues')}
                    </Typography>
                  </Stack>
                  <Typography variant="h3" sx={{ fontWeight: 800 }}>
                    {status?.listingsDeactivated ?? 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card sx={statCardSx}>
                <CardContent>
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
                    <VisibilityOffIcon color="warning" />
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {t('activitySuspension.stats.products', 'Produits suspendus')}
                    </Typography>
                  </Stack>
                  <Typography variant="h3" sx={{ fontWeight: 800 }}>
                    {status?.productsDeactivated ?? 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <TextField
            fullWidth
            multiline
            minRows={3}
            label={t('activitySuspension.reasonLabel', 'Motif facultatif')}
            placeholder={t('activitySuspension.reasonPlaceholder', 'Exemple : congés, vacances, fermeture temporaire, indisponibilité logistique...')}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            sx={{ mb: 3 }}
          />

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            {!status?.isSuspended ? (
              <Button
                variant="contained"
                color="warning"
                startIcon={<PauseCircleIcon />}
                disabled={submitting}
                onClick={handleSuspend}
              >
                {t('activitySuspension.actions.suspend', 'Suspendre mon activité')}
              </Button>
            ) : (
              <Button
                variant="contained"
                color="success"
                startIcon={<PlayCircleIcon />}
                disabled={submitting}
                onClick={handleReactivate}
              >
                {t('activitySuspension.actions.reactivate', 'Réactiver mon activité')}
              </Button>
            )}
            
          </Stack>
        </CardContent>
      </Card>

      <ListingProductSidebar activePage="activity-suspension" user={user} />
    </Container>
  );
};

export default ActivitySuspensionPage;